import {MvsCoreService} from "../../../../../../core/shared/service/mvs-core.service";
import {MvsCrudService} from "../../../../../../core/shared/service/crud/mvs-crud.service";
import {Component, OnInit} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {IntegrationTypeStepDto} from "../../../../model/dto/entity/integration-type-step.dto";
import {IntegrationTypeStepInputDto} from "../../../../model/dto/entity/integration-type-step-input.dto";
import {IntegrationTypeStepOutputDto} from "../../../../model/dto/entity/integration-type-step-output.dto";
import {IntegrationTypeStepTransitionDto} from "../../../../model/dto/entity/integration-type-step-transition.dto";
import {ObjectBaseModeComponent} from "../../../../../../core/shared/object/mvs-object-base/object-base-mode.component";
import {WidgetData} from "../../../../../../core/shared/widget/widget-data";
import {MvsUiObjectService} from "../../../../../../core/shared/object/service/mvs-ui-object.service";
import {WidgetFactory} from "../../../../../../core/shared/widget/service/widget.factory";
import {NavigationItem} from "../../../../../../core/shared/dto/navigation/navigation-item";
import {FilterCriteria} from "../../../../../../core/shared/filter/api/filter.criteria";
import {ObjectRequestList} from "../../../../../../core/shared/dto/object/request/object-request-list";
import {ObjectRequestComplex} from "../../../../../../core/shared/dto/object/request/object-request-complex";
import {ObjectRequestComplexNode} from "../../../../../../core/shared/dto/object/request/object-request-complex-node";
import {ObjectRequestRelation} from "../../../../../../core/shared/dto/object/request/object-request-relation";
import {DtoDetail} from "../../../../../../core/shared/dto/dto.detail";
import {MvsFormControlOverwrite} from "../../../../../../core/shared/logic/form/logic/mvs-form-control-overwrite";
import {MvsFormFieldAccessEnum} from "../../../../../../core/shared/logic/form/logic/mvs-form-field-access.enum";
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from "../../../../../../core/shared/logic/object-change-information";
import {MvsCrudModeEnum} from "../../../../../../core/shared/service/crud/mvs-crud-mode.enum";
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from "../../../../../../core/shared/helper/widget-function-call-back-create";
import {WidgetDataParam} from "../../../../../../core/shared/widget/widget-data-param";
import {ObjectIdentifierData} from "../../../../../../core/shared/basic/object-identifier-data";
import {ObjectIdentifier} from "../../../../../../core/shared/basic/object-identifier";

/**
 * IntegrationTypeStepBaseComponent
 *
 * Base component for IntegrationTypeStep view modes (side/full).
 * Extends ObjectBaseModeComponent and provides shared functionality for:
 * - Loading IntegrationTypeStep with related entities
 * - Managing widgets for step info, inputs, outputs, and transitions
 * - Handling CRUD operations for child entities
 *
 * Child entities:
 * - hc.IntegrationTypeStepInput (step FK)
 * - hc.IntegrationTypeStepOutput (step FK)
 * - hc.IntegrationTypeStepTransition (fromStep/toStep FK)
 *
 * @see IntegrationTypeStepSideComponent - Side panel implementation
 * @see IntegrationTypeStepFullComponent - Full page implementation
 */
@Component({
    selector: 'hc-integration-type-step-base-component',
    template: '',
    standalone: false
})
export class IntegrationTypeStepBaseComponent extends ObjectBaseModeComponent implements OnInit {

    integrationTypeStepService: MvsCrudService;
    integrationTypeStepInputService: MvsCrudService;
    integrationTypeStepOutputService: MvsCrudService;
    integrationTypeStepTransitionService: MvsCrudService;

    integrationTypeStepDto: IntegrationTypeStepDto;

    // Widgets for step info
    integrationTypeStepWidget: WidgetData;

    // Widgets for step inputs
    stepInputTableWidget: WidgetData;
    stepInputObjectWidget: WidgetData;

    // Widgets for step outputs
    stepOutputTableWidget: WidgetData;
    stepOutputObjectWidget: WidgetData;

    // Widgets for step transitions
    stepTransitionFromTableWidget: WidgetData;
    stepTransitionToTableWidget: WidgetData;
    stepTransitionObjectWidget: WidgetData;

    constructor(
        protected coreService: MvsCoreService,
        protected route: ActivatedRoute,
        protected objectService: MvsUiObjectService) {

        super(route);
    }

    ngOnInit() {
        this.activeNavigationItem = this.navigationItems[0];
        this.onNavigationItems.emit(this.navigationItems);
        super.ngOnInit();

        // Initialize services
        this.integrationTypeStepService = this.coreService.getCrudService("hc.IntegrationTypeStep");
        this.integrationTypeStepInputService = this.coreService.getCrudService("hc.IntegrationTypeStepInput");
        this.integrationTypeStepOutputService = this.coreService.getCrudService("hc.IntegrationTypeStepOutput");
        this.integrationTypeStepTransitionService = this.coreService.getCrudService("hc.IntegrationTypeStepTransition");

        // Initialize widgets
        this.refreshStepInfoWidget();

        this.initialized = true;
    }

    /**
     * Handle navigation item change - load data for selected section
     */
    onNavigationItemChange(navigationItem: NavigationItem) {
        if (navigationItem?.action == 'stepInputs') {
            this.refreshStepInputTableWidget();
        } else if (navigationItem?.action == 'stepOutputs') {
            this.refreshStepOutputTableWidget();
        } else if (navigationItem?.action == 'stepTransitions') {
            this.refreshStepTransitionWidgets();
        }
    }

    /**
     * Refresh the main step info widget
     */
    refreshStepInfoWidget() {
        this.integrationTypeStepWidget = WidgetFactory.createWidgetEntityData(
            'hc.integration.type.step.data.widget',
            'Integrationsschritt',
            'hc.IntegrationTypeStep',
            this.objectIdentifier.objectId
        );
    }

    /**
     * Refresh the step inputs table widget
     */
    refreshStepInputTableWidget() {
        const filterCriteria = FilterCriteria.create('e.stepId', FilterCriteria.cOperatorEqual, this.objectIdentifier.objectId);

        this.stepInputTableWidget = WidgetFactory.createWidgetTableEntityBasicQl(
            'hc.integration.type.step.input.table.widget',
            'Schritt-Eingaben',
            'hc.IntegrationTypeStepInput',
            [filterCriteria]
        );

        const fields: WidgetToolbarCreateInterface[] = [{
            fieldName: 'stepDtoId',
            fieldValue: this.objectIdentifier.objectId
        }];
        this.stepInputTableWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fields);
    }

    /**
     * Refresh the step outputs table widget
     */
    refreshStepOutputTableWidget() {
        const filterCriteria = FilterCriteria.create('e.stepId', FilterCriteria.cOperatorEqual, this.objectIdentifier.objectId);

        this.stepOutputTableWidget = WidgetFactory.createWidgetTableEntityBasicQl(
            'hc.integration.type.step.output.table.widget',
            'Schritt-Ausgaben',
            'hc.IntegrationTypeStepOutput',
            [filterCriteria]
        );

        const fields: WidgetToolbarCreateInterface[] = [{
            fieldName: 'stepDtoId',
            fieldValue: this.objectIdentifier.objectId
        }];
        this.stepOutputTableWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fields);
    }

    /**
     * Refresh the step transitions widgets (from and to)
     */
    refreshStepTransitionWidgets() {
        // Transitions FROM this step
        const fromFilterCriteria = FilterCriteria.create('e.fromStepId', FilterCriteria.cOperatorEqual, this.objectIdentifier.objectId);

        this.stepTransitionFromTableWidget = WidgetFactory.createWidgetTableEntityBasicQl(
            'hc.integration.type.step.transition.from.table.widget',
            'Ausgehende Übergänge',
            'hc.IntegrationTypeStepTransition',
            [fromFilterCriteria]
        );

        // Transitions TO this step
        const toFilterCriteria = FilterCriteria.create('e.toStepId', FilterCriteria.cOperatorEqual, this.objectIdentifier.objectId);

        this.stepTransitionToTableWidget = WidgetFactory.createWidgetTableEntityBasicQl(
            'hc.integration.type.step.transition.to.table.widget',
            'Eingehende Übergänge',
            'hc.IntegrationTypeStepTransition',
            [toFilterCriteria]
        );
    }

    /**
     * Handle step input selection from table widget
     */
    handleStepInputSelect(event: ObjectIdentifierData | ObjectIdentifier) {
        const defaultDto = new DtoDetail();
        defaultDto['stepDtoId'] = this.objectIdentifier.objectId;

        const formControlOverwrite = new MvsFormControlOverwrite();
        formControlOverwrite.addField('stepDtoId', MvsFormFieldAccessEnum.READ);

        this.stepInputObjectWidget = WidgetFactory.createWidgetObject(
            'hc.integration.type.step.input.object.widget',
            'Schritt-Eingabe',
            event.objectType,
            event.objectId,
            WidgetDataParam.create('createDefaultDto', defaultDto),
            WidgetDataParam.create('formControlOverwrite', formControlOverwrite)
        );
    }

    /**
     * Handle step output selection from table widget
     */
    handleStepOutputSelect(event: ObjectIdentifierData | ObjectIdentifier) {
        const defaultDto = new DtoDetail();
        defaultDto['stepDtoId'] = this.objectIdentifier.objectId;

        const formControlOverwrite = new MvsFormControlOverwrite();
        formControlOverwrite.addField('stepDtoId', MvsFormFieldAccessEnum.READ);

        this.stepOutputObjectWidget = WidgetFactory.createWidgetObject(
            'hc.integration.type.step.output.object.widget',
            'Schritt-Ausgabe',
            event.objectType,
            event.objectId,
            WidgetDataParam.create('createDefaultDto', defaultDto),
            WidgetDataParam.create('formControlOverwrite', formControlOverwrite)
        );
    }

    /**
     * Handle step transition selection from table widget
     */
    handleStepTransitionSelect(event: ObjectIdentifierData | ObjectIdentifier) {
        const formControlOverwrite = new MvsFormControlOverwrite();

        this.stepTransitionObjectWidget = WidgetFactory.createWidgetObject(
            'hc.integration.type.step.transition.object.widget',
            'Schritt-Übergang',
            event.objectType,
            event.objectId,
            WidgetDataParam.create('formControlOverwrite', formControlOverwrite)
        );
    }

    /**
     * Open dialog to create/edit a step input
     */
    handleStepInput(stepInput?: IntegrationTypeStepInputDto) {
        const id = stepInput?.id ? stepInput?.id : 0;

        const defaultDto = new DtoDetail();
        defaultDto['stepDtoId'] = this.objectIdentifier.objectId;

        const formControlOverwrite = new MvsFormControlOverwrite();
        formControlOverwrite.addField('stepDtoId', MvsFormFieldAccessEnum.READ);

        this.objectService.openObjectViaDialog(
            null,
            "hc.IntegrationTypeStepInput",
            id,
            defaultDto,
            false,
            false,
            () => {
                this.refreshStepInputTableWidget();
            },
            MvsCrudModeEnum.create,
            null,
            formControlOverwrite
        );
    }

    /**
     * Open dialog to create/edit a step output
     */
    handleStepOutput(stepOutput?: IntegrationTypeStepOutputDto) {
        const id = stepOutput?.id ? stepOutput?.id : 0;

        const defaultDto = new DtoDetail();
        defaultDto['stepDtoId'] = this.objectIdentifier.objectId;

        const formControlOverwrite = new MvsFormControlOverwrite();
        formControlOverwrite.addField('stepDtoId', MvsFormFieldAccessEnum.READ);

        this.objectService.openObjectViaDialog(
            null,
            "hc.IntegrationTypeStepOutput",
            id,
            defaultDto,
            false,
            false,
            () => {
                this.refreshStepOutputTableWidget();
            },
            MvsCrudModeEnum.create,
            null,
            formControlOverwrite
        );
    }

    /**
     * Open dialog to create/edit a step transition (from this step)
     */
    handleStepTransitionFrom() {
        const defaultDto = new DtoDetail();
        defaultDto['fromStepDtoId'] = this.objectIdentifier.objectId;

        const formControlOverwrite = new MvsFormControlOverwrite();
        formControlOverwrite.addField('fromStepDtoId', MvsFormFieldAccessEnum.READ);

        this.objectService.openObjectViaDialog(
            null,
            "hc.IntegrationTypeStepTransition",
            0,
            defaultDto,
            false,
            false,
            () => {
                this.refreshStepTransitionWidgets();
            },
            MvsCrudModeEnum.create,
            null,
            formControlOverwrite
        );
    }

}
